import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ExternalLink, Loader2, Plus, Trash2, CreditCard } from "lucide-react";
import { type BusinessStatus, type BusinessKind } from "@/lib/admin-labels";
import { LanguageSwitcher, tKind, tStatus, useT } from "@/lib/i18n";
import { dt, csMonthWord } from "@/lib/dashboard-i18n";
import { ReferralPanel } from "@/components/ReferralPanel";
import { SuppliersSection } from "@/components/SuppliersSection";
import {
  createAdCheckout,
  createSubscriptionCheckout,
  createLifetimeCheckout,
  createPhotoPackCheckout,
  createPortalSession,
} from "@/lib/checkout.functions";
import { getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe.ts";
import {
  getMySubscriptionOverview,
  syncMyPayments,
  getMySubscriptionHistory,
} from "@/lib/subscription-sync.functions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings2, Infinity as InfinityIcon, Calendar, RefreshCw, History } from "lucide-react";

export const Route = createFileRoute("/moje-podniky/")({
  ssr: false,
  component: MyBusinessesPage,
  head: () => ({
    meta: [
      { title: "Moje podniky — Cerveza.International" },
      { name: "description", content: "Správa vašich podniků v katalogu Cerveza.International." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

const FREE_HOST_RE = /^https?:\/\/([a-z0-9-]+\.)*(directclub\.digital|direct\.club)(\/|$|\?|#)/i;
const urlSchema = (msg: string) => z.string().trim().url(msg).max(2048);

function isFreeUrl(u: string) {
  return FREE_HOST_RE.test(u.trim());
}

function MyBusinessesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/auth", search: { redirect: "/moje-podniky" } });
      return;
    }
  
  }, [authLoading, user, navigate]);

  const { data: businesses = [], isLoading } = useQuery({
    queryKey: ["my-businesses", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("businesses")
        .select("id, name, kind, city, country, status")
        .eq("owner_id", user!.id)
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: pricing } = useQuery({
    queryKey: ["pricing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pricing_tiers")
        .select("item_type, min_quantity, price_cents, currency, active")
        .eq("active", true)
        .order("min_quantity", { ascending: true });
      if (error) throw error;
      const linkTier = data?.find((t) => t.item_type === "link");
      const photoTier = data?.find((t) => t.item_type === "photo");
      return {
        price_cents: linkTier?.price_cents ?? 0,
        photo_price_cents: photoTier?.price_cents ?? 0,
        currency: linkTier?.currency ?? photoTier?.currency ?? "EUR",
      };
    },
  });

  if (authLoading || !user) {
    return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">{t("my.loading")}</div>;
  }

  return (
    <div className="min-h-screen bg-muted/30 px-3 py-6 sm:px-4 sm:py-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-2xl sm:text-3xl">{t("my.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("my.sub")}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <LanguageSwitcher />
            <Button asChild size="sm">
              <Link to="/registrace-podniku"><Plus className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">{t("my.add")}</span></Link>
            </Button>
          </div>
        </div>

        {isLoading && <div className="text-sm text-muted-foreground">{t("my.loading")}</div>}
        {!isLoading && businesses.length === 0 && (
          <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t("my.empty.a")} <Link to="/registrace-podniku" className="text-primary hover:underline">{t("my.empty.b")}</Link>.
          </CardContent></Card>
        )}

        {businesses.map((b) => (
          <BusinessCard key={b.id} business={b} pricing={pricing} ownerId={user!.id} />
        ))}

        {user && <SubscriptionsSection />}

        {user && <ReferralPanel userId={user.id} />}
      </div>
    </div>
  );
}


type Business = {
  id: string; name: string; kind: BusinessKind; city: string; country: string; status: BusinessStatus;
};

type Pricing = { price_cents: number; photo_price_cents?: number; currency: string };

function BusinessCard({ business, pricing, ownerId }: { business: Business; pricing?: Pricing | null; ownerId: string }) {
  const qc = useQueryClient();
  const { t, lang } = useT();
  const T = (k: string, v?: Record<string, string | number>) => dt(lang, k, v);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");

  const { data: links = [] } = useQuery({
    queryKey: ["my-links", business.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_links")
        .select("id, url, label, is_free, status, paid_at, expires_at, promo_months")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const freeCount = links.filter((l) => l.is_free).length;
  const trimmedUrl = url.trim();
  const willBeFree = trimmedUrl ? isFreeUrl(trimmedUrl) : false;
  const freeLimitReached = willBeFree && freeCount >= 3;

  const add = useMutation({
    mutationFn: async () => {
      const parsed = urlSchema(t("my.invalidUrl")).parse(trimmedUrl);
      const { error } = await supabase.from("business_links").insert({
        business_id: business.id,
        url: parsed,
        label: label.trim() || null,
        created_by: ownerId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setUrl(""); setLabel("");
      qc.invalidateQueries({ queryKey: ["my-links", business.id] });
      toast.success(willBeFree ? t("my.freeAdded") : t("my.paidPending"));
    },
    onError: (e: Error) => {
      const msg = e instanceof z.ZodError ? e.errors[0]?.message ?? t("my.invalidUrl") : e.message;
      toast.error(msg);
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("business_links").delete().eq("id", id).eq("business_id", business.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-links", business.id] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const checkout = useServerFn(createAdCheckout);
  const subCheckout = useServerFn(createSubscriptionCheckout);
  const lifeCheckout = useServerFn(createLifetimeCheckout);
  const portal = useServerFn(createPortalSession);

  const pay = useMutation({
    mutationFn: async (vars: {
      mode: "single" | "monthly" | "yearly" | "lifetime";
      sourceId: string;
    }) => {
      const env = getStripeEnvironment();
      const origin = window.location.origin;
      let res: { url: string } | { error: string };
      if (vars.mode === "single") {
        res = await checkout({
          data: { kind: "link", sourceId: vars.sourceId, origin, environment: env },
        });
      } else if (vars.mode === "lifetime") {
        res = await lifeCheckout({
          data: { businessLinkId: vars.sourceId, origin, environment: env },
        });
      } else {
        res = await subCheckout({
          data: {
            businessLinkId: vars.sourceId,
            interval: vars.mode,
            origin,
            environment: env,
          },
        });
      }
      if ("error" in res) throw new Error(res.error);
      window.location.href = res.url;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openPortal = useMutation({
    mutationFn: async () => {
      const res = await portal({
        data: { origin: window.location.origin, environment: getStripeEnvironment() },
      });
      if ("error" in res) throw new Error(res.error);
      window.open(res.url, "_blank", "noopener");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const priceFmt = pricing
    ? `${(pricing.price_cents / 100).toLocaleString("cs-CZ", { minimumFractionDigits: 2 })} ${pricing.currency}`
    : "—";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="font-serif">{business.name}</CardTitle>
            <CardDescription>
              {tKind(t, business.kind)} · {business.city}, {business.country}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to="/moje-podniky/$id/menu" params={{ id: business.id }}>Menu</Link>
            </Button>
            <Badge variant={business.status === "approved" ? "default" : "outline"}>
              {tStatus(t, business.status)}
            </Badge>
            {isPaymentsConfigured() && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => openPortal.mutate()}
                disabled={openPortal.isPending}
                title={T("my.managePortal")}
              >
                {openPortal.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Settings2 className="mr-1 h-4 w-4" />
                )}
                {T("my.managePortal")}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <h3 className="text-sm font-medium mb-2">{t("my.links")} ({links.length})</h3>
          {links.length === 0 && <p className="text-sm text-muted-foreground">{t("my.noLinks")}</p>}
          <ul className="space-y-2">
            {links.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2">
                <div className="min-w-0 flex-1">
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline truncate">
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">{l.label || l.url}</span>
                  </a>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {l.is_free ? <Badge variant="secondary">{t("my.free")}</Badge> : <Badge variant="outline">{t("my.paid")}</Badge>}
                    <Badge variant={l.status === "active" ? "default" : l.status === "rejected" ? "destructive" : "outline"}>
                      {l.status === "active" ? t("my.active") : l.status === "rejected" ? t("my.rejected") : t("my.pendingPayment")}
                    </Badge>
                    {l.promo_months > 0 && l.expires_at && (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                        🎁 {T("my.promoFree", {
                          n: l.promo_months,
                          unit: lang === "cs" ? csMonthWord(l.promo_months) : T("my.month"),
                          date: new Date(l.expires_at).toLocaleDateString(`${lang}-${lang.toUpperCase()}`),
                        })}
                      </Badge>
                    )}
                    {l.expires_at && new Date(l.expires_at) < new Date() && (
                      <Badge variant="destructive">{T("my.promoExpired")}</Badge>
                    )}
                  </div>
                </div>
                {l.status === "pending_payment" && !l.is_free && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="default"
                        disabled={!isPaymentsConfigured() || (pay.isPending && pay.variables?.sourceId === l.id)}
                        title={isPaymentsConfigured() ? t("my.pay") : t("my.payTooltip")}
                      >
                        {pay.isPending && pay.variables?.sourceId === l.id ? (
                          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="mr-1 h-4 w-4" />
                        )}
                        {t("my.pay")}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => pay.mutate({ mode: "monthly", sourceId: l.id })}>
                        <RefreshCw className="mr-2 h-4 w-4" /> {T("my.payMonthly")} (9.99 €)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => pay.mutate({ mode: "yearly", sourceId: l.id })}>
                        <Calendar className="mr-2 h-4 w-4" /> {T("my.payYearly")} (99.99 €)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => pay.mutate({ mode: "lifetime", sourceId: l.id })}>
                        <InfinityIcon className="mr-2 h-4 w-4" /> {T("my.payLifetime")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => pay.mutate({ mode: "single", sourceId: l.id })}>
                        <CreditCard className="mr-2 h-4 w-4" /> {T("my.payOnce")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button size="icon" variant="ghost" onClick={() => del.mutate(l.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-md border bg-muted/30 p-3 space-y-3">
          <div className="text-sm font-medium">{t("my.addLink")}</div>
          <div
            className="text-xs text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: t("my.linkInfo", { price: priceFmt }) }}
          />
          <div className="grid gap-2 sm:grid-cols-[1fr_180px_auto]">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("my.urlPlaceholder")} />
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("my.labelPlaceholder")} />
            <Button onClick={() => add.mutate()} disabled={add.isPending || !trimmedUrl || freeLimitReached}>
              {add.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {willBeFree ? t("my.addFree") : t("my.addPaid", { price: priceFmt })}
            </Button>
          </div>
          {trimmedUrl && (
            <div className="text-xs">
              {freeLimitReached
                ? <span className="text-destructive">{t("my.freeLimit")}</span>
                : willBeFree
                  ? <span className="text-primary">{t("my.willBeFree")}</span>
                  : <span className="text-muted-foreground">{t("my.willBePaid", { price: priceFmt })}</span>}
            </div>
          )}
        </div>

        <PhotosSection businessId={business.id} pricing={pricing} />
        <div className="mt-6 border-t pt-4">
          <SuppliersSection businessId={business.id} />
        </div>
      </CardContent>
    </Card>
  );
}

function PhotosSection({ businessId, pricing }: { businessId: string; pricing?: Pricing | null }) {
  const qc = useQueryClient();
  const { t } = useT();
  const [uploading, setUploading] = useState(false);
  const checkout = useServerFn(createAdCheckout);
  const pay = useMutation({
    mutationFn: async (sourceId: string) => {
      const res = await checkout({
        data: {
          kind: "photo",
          sourceId,
          origin: window.location.origin,
          environment: getStripeEnvironment(),
        },
      });
      if ("error" in res) throw new Error(res.error);
      window.location.href = res.url;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const packCheckout = useServerFn(createPhotoPackCheckout);
  const buyPack = useMutation({
    mutationFn: async (pack_size: 5 | 10 | 20) => {
      const res = await packCheckout({
        data: {
          businessId,
          packSize: pack_size,
          origin: window.location.origin,
          environment: getStripeEnvironment(),
        },
      });
      if ("error" in res) throw new Error(res.error);
      window.location.href = res.url;
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const photoPriceFmt = pricing
    ? `${((pricing.photo_price_cents ?? 0) / 100).toLocaleString("cs-CZ", { minimumFractionDigits: 2 })} ${pricing.currency}`
    : "—";

  const { data: photos = [] } = useQuery({
    queryKey: ["my-photos", businessId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_photos")
        .select("id, storage_path, is_free, status, rejection_reason, created_at")
        .eq("business_id", businessId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: signedMap = {} } = useQuery({
    queryKey: ["my-photos-signed", businessId, photos.map((p) => p.id).join(",")],
    enabled: photos.length > 0,
    queryFn: async () => {
      const map: Record<string, string> = {};
      await Promise.all(photos.map(async (p) => {
        const { data } = await supabase.storage
          .from("business-photos")
          .createSignedUrl(p.storage_path, 60 * 60, {
            transform: { width: 320, height: 320, quality: 65, resize: "cover" },
          });
        if (data?.signedUrl) map[p.storage_path] = data.signedUrl;
      }));
      return map;
    },
  });

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error(t("photos.invalidType"));
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error(t("photos.tooLarge"));
      return;
    }
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
      const path = `${businessId}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("business-photos")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("business_photos").insert({
        business_id: businessId,
        storage_path: path,
        size_bytes: file.size,
        mime_type: file.type,
      });
      if (insErr) {
        await supabase.storage.from("business-photos").remove([path]);
        throw insErr;
      }
      const willBeFree = photos.length < 3;
      toast.success(willBeFree ? t("my.photoUploadedPending") : t("photos.paidPending"));
      qc.invalidateQueries({ queryKey: ["my-photos", businessId] });
    } catch (e) {
      toast.error(t("photos.uploadFailed", { msg: (e as Error).message }));
    } finally {
      setUploading(false);
    }
  };

  const del = useMutation({
    mutationFn: async (p: { id: string; storage_path: string }) => {
      await supabase.storage.from("business-photos").remove([p.storage_path]);
      const { error } = await supabase.from("business_photos").delete().eq("id", p.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("photos.deleted"));
      qc.invalidateQueries({ queryKey: ["my-photos", businessId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const freeCount = photos.filter((p) => p.is_free).length;

  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{t("photos.title")} ({photos.length})</div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent">
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          {t("photos.add")}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />
        </label>
      </div>
      <p className="text-xs text-muted-foreground">{t("photos.maxSize", { price: photoPriceFmt })}</p>
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map((p) => (
            <div key={p.id} className="space-y-1">
              <div className="group relative aspect-square overflow-hidden rounded-md border bg-background">
                {signedMap[p.storage_path] ? (
                  <img src={signedMap[p.storage_path]} alt="" loading="lazy" decoding="async" width={320} height={320} className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full w-full place-items-center text-xs text-muted-foreground">…</div>
                )}
                <div className="absolute left-1 top-1 flex gap-1">
                  {p.status === "active" ? (
                    <Badge variant="default" className="text-[10px]">
                      {p.is_free ? t("my.free") : t("my.paid")}
                    </Badge>
                  ) : p.status === "rejected" ? (
                    <Badge variant="destructive" className="text-[10px]">{t("my.rejectedShort")}</Badge>
                  ) : p.status === "pending_payment" ? (
                    <Badge variant="outline" className="text-[10px]">{t("my.pendingPayment")}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">{t("my.photoPendingApproval")}</Badge>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => del.mutate({ id: p.id, storage_path: p.storage_path })}
                  className="absolute right-1 top-1 rounded bg-background/80 p-1 opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
              {p.status === "rejected" && p.rejection_reason && (
                <p className="text-[11px] leading-tight text-destructive">
                  {t("my.rejectReason", { reason: p.rejection_reason })}
                </p>
              )}
              {p.status === "pending_payment" && !p.is_free && (
                <Button
                  size="sm"
                  variant="default"
                  className="w-full h-7 text-[11px]"
                  disabled={!isPaymentsConfigured() || (pay.isPending && pay.variables === p.id)}
                  onClick={() => pay.mutate(p.id)}
                >
                  {pay.isPending && pay.variables === p.id ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <CreditCard className="mr-1 h-3 w-3" />
                  )}
                  {t("my.pay")}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-2">
        <div className="text-xs text-muted-foreground">
          {t("my.freeOutOf", { free: freeCount, price: photoPriceFmt })}
        </div>
        {isPaymentsConfigured() && (
          <div className="flex flex-wrap gap-1">
            <span className="text-xs text-muted-foreground self-center mr-1">{t("my.photoPack")}</span>
            {[5, 10, 20].map((n) => (
              <Button
                key={n}
                size="sm"
                variant="outline"
                className="h-7 text-[11px]"
                disabled={buyPack.isPending && buyPack.variables === n}
                onClick={() => buyPack.mutate(n as 5 | 10 | 20)}
              >
                {buyPack.isPending && buyPack.variables === n ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Plus className="mr-1 h-3 w-3" />
                )}
                {n} {t("my.pieces")}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function SubscriptionsSection() {
  const qc = useQueryClient();
  const { t, lang } = useT();
  const T = (k: string, v?: Record<string, string | number>) => dt(lang, k, v);
  const overviewFn = useServerFn(getMySubscriptionOverview);
  const syncFn = useServerFn(syncMyPayments);
  const syncedOnce = useRef(false);
  const env = getStripeEnvironment();

  const overview = useQuery({
    queryKey: ["my-subscriptions", env],
    queryFn: () => overviewFn({ data: { environment: env } }),
    refetchInterval: 60_000,
  });

  const sync = useMutation({
    mutationFn: () => syncFn({ data: { environment: env } }),
    onSuccess: (res) => {
      if ("error" in res) {
        toast.error(res.error);
        return;
      }
      if (res.activated > 0) {
        toast.success(T("subs.synced", { n: res.activated }));
      }
      qc.invalidateQueries({ queryKey: ["my-subscriptions"] });
      qc.invalidateQueries({ queryKey: ["my-links"] });
      qc.invalidateQueries({ queryKey: ["my-businesses"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Auto-heal on page load: ask Stripe about pending payments once.
  useEffect(() => {
    if (syncedOnce.current) return;
    syncedOnce.current = true;
    sync.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isPaymentsConfigured()) return null;

  const data = overview.data;
  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(`${lang}-${lang.toUpperCase()}`) : "—";
  const priceLabel = (priceId: string | null) =>
    priceId === "ad_link_monthly"
      ? T("subs.monthly")
      : priceId === "ad_link_yearly"
        ? T("subs.yearly")
        : T("subs.adLink");
  const kindLabel = (kind: string) =>
    kind === "ad_link_subscription"
      ? T("subs.monthly")
      : kind === "ad_link_lifetime"
        ? T("subs.lifetime")
        : T("subs.oneTime");

  const empty =
    data && data.active.length === 0 && data.pending.length === 0 && data.pendingOrders.length === 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="font-serif text-lg">{T("subs.title")}</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
          >
            {sync.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-4 w-4" />
            )}
            {T("subs.checkNow")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {sync.isPending && (
          <p className="text-xs text-muted-foreground">{T("subs.syncing")}</p>
        )}
        {overview.isLoading && (
          <p className="text-sm text-muted-foreground">{t("my.loading")}</p>
        )}
        {empty && <p className="text-sm text-muted-foreground">{T("subs.none")}</p>}
        {data?.stripeError && (
          <p className="text-xs text-destructive">
            {T("subs.stripeError", { msg: data.stripeError })}
          </p>
        )}

        {data?.active.map((s) => (
          <div
            key={s.id}
            className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">
                {s.link_label || s.link_url || priceLabel(s.price_id)}
                {s.business_name && (
                  <span className="text-muted-foreground"> · {s.business_name}</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {priceLabel(s.price_id)}
                {s.current_period_end &&
                  (s.cancel_at_period_end
                    ? ` · ${T("subs.cancelsAt", { date: fmtDate(s.current_period_end) })}`
                    : ` · ${T("subs.until", { date: fmtDate(s.current_period_end) })}`)}
                {s.business_link_id &&
                  ` · ${s.link_status === "active" ? T("subs.linkActive") : T("subs.linkInactive")}`}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Badge variant={s.source === "stripe" ? "secondary" : "outline"}>
                {s.source === "stripe" ? T("subs.verified") : T("subs.unverified")}
              </Badge>
              <Badge>{T("subs.active")}</Badge>
            </div>
          </div>
        ))}

        {data?.pending.map((s) => (
          <div
            key={s.id}
            className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">
                {s.link_label || s.link_url || priceLabel(s.price_id)}
                {s.business_name && (
                  <span className="text-muted-foreground"> · {s.business_name}</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">{priceLabel(s.price_id)}</div>
            </div>
            <Badge variant="outline">{T("subs.pending")}</Badge>
          </div>
        ))}

        {data && data.pendingOrders.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">{T("subs.pendingPayments")}</div>
            {data.pendingOrders.map((o) => (
              <div
                key={o.id}
                className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {kindLabel(o.kind)}
                    {o.business_name && (
                      <span className="text-muted-foreground"> · {o.business_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {(o.amount_cents / 100).toLocaleString("cs-CZ", { minimumFractionDigits: 2 })}{" "}
                    {o.currency} · {fmtDate(o.created_at)}
                  </div>
                </div>
                <Badge variant="outline">{T("subs.pending")}</Badge>
              </div>
            ))}
          </div>
        )}

        {data && data.activeLinks.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">{T("subs.liveLinks")}</div>
            {data.activeLinks.map((l) => (
              <div
                key={l.id}
                className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {l.label || l.url}
                    {l.business_name && (
                      <span className="text-muted-foreground"> · {l.business_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {l.expires_at ? T("subs.until", { date: fmtDate(l.expires_at) }) : T("subs.lifetime")}
                  </div>
                </div>
                <Badge>{T("subs.linkActive")}</Badge>
              </div>
            ))}
          </div>
        )}

        <SubscriptionHistory env={env} />
      </CardContent>
    </Card>
  );
}

function SubscriptionHistory({ env }: { env: ReturnType<typeof getStripeEnvironment> }) {
  const { lang } = useT();
  const T = (k: string, v?: Record<string, string | number>) => dt(lang, k, v);
  const [open, setOpen] = useState(false);
  const historyFn = useServerFn(getMySubscriptionHistory);

  const history = useQuery({
    queryKey: ["my-subscription-history", env],
    queryFn: () => historyFn({ data: { environment: env } }),
    enabled: open,
  });

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(`${lang}-${lang.toUpperCase()}`) : "—";

  return (
    <div className="border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <History className="h-4 w-4 text-muted-foreground" />
          {T("subs.history")}
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)}>
          {open ? T("subs.historyHide") : T("subs.historyShow")}
        </Button>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          {history.isLoading && (
            <p className="text-xs text-muted-foreground">{T("subs.syncing")}</p>
          )}
          {history.data?.length === 0 && (
            <p className="text-xs text-muted-foreground">{T("subs.historyNone")}</p>
          )}
          {history.data?.map((e) => (
            <div
              key={e.id}
              className={`rounded-md border px-3 py-2 ${
                e.kind === "failure" || e.new_status === "past_due"
                  ? "border-destructive/40 bg-destructive/5"
                  : "bg-background"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    {e.kind === "failure"
                      ? T("subs.evtFailure")
                      : e.old_status
                        ? T("subs.evtChange", { from: e.old_status, to: e.new_status ?? "—" })
                        : T("subs.evtStart", { to: e.new_status ?? "—" })}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {e.reason || (e.kind === "failure" ? T("subs.noReason") : e.source || "")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {e.amount_cents != null &&
                      `${(e.amount_cents / 100).toLocaleString("cs-CZ", {
                        minimumFractionDigits: 2,
                      })} ${e.currency ?? "EUR"} · `}
                    {e.attempt_count != null && `${T("subs.attempt", { n: e.attempt_count })} · `}
                    {e.next_payment_attempt &&
                      `${T("subs.nextAttempt", { date: fmt(e.next_payment_attempt) })}`}
                  </div>
                  {e.hosted_invoice_url && (
                    <a
                      href={e.hosted_invoice_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary underline"
                    >
                      {T("subs.invoice")} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{fmt(e.at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

